import React, { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { Channel } from "../../../../lib/api/channels";
import { format } from "date-fns";

interface ChannelDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    channel: Channel | null;
    onReload: (channelId: string) => Promise<void>;
    onDelete: (channelId: string, channelName: string) => void;
    onReconnect: (channel: Channel) => void;
    syncStatusMessage?: string;
}

export const ChannelDetailModal: React.FC<ChannelDetailModalProps> = ({
    isOpen,
    onClose,
    channel,
    onReload,
    onDelete,
    onReconnect,
    syncStatusMessage
}) => {
    const { t, i18n } = useTranslation("common");
    const [isReloading, setIsReloading] = useState(false);

    if (!channel) return null;

    const handleReload = async () => {
        setIsReloading(true);
        try {
            await onReload(channel.id);
        } finally {
            setIsReloading(false);
        }
    };

    // Mirror ChannelContextMenu: hide reload when cooldown (30 min) hasn't elapsed
    // so the modal and the item menu stay in sync.
    const isDisconnectedOrUninstalled = channel.status === 'disconnected' || channel.status === 'uninstalled';
    const canReload = !isDisconnectedOrUninstalled && (process.env.REMOVE_RESTRICTIONS === 'true' || !channel.lastReloadedAt || (Date.now() - new Date(channel.lastReloadedAt).getTime() > 30 * 60 * 1000));

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case "active":
                return "bg-success-100 text-success-700 border-success-200";
            case "syncing":
                return "bg-warning-100 text-warning-700 border-warning-200";
            case "error":
                return "bg-error-100 text-error-700 border-error-200";
            case "disconnected":
            case "uninstalled":
                return "bg-orange-100 text-orange-700 border-orange-200";
            case "inactive":
                return "bg-neutral-100 text-neutral-600 border-neutral-200";
            default:
                return "bg-neutral-100 text-neutral-600 border-neutral-200";
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status.toLowerCase()) {
            case "active":
                return (
                    <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_2ef72bf2.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_2ef72bf2.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                );
            case "syncing":
                return (
                    <span className="w-4 h-4 animate-spin text-current" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_be8c64a0.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_be8c64a0.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                );
            case "error":
                return (
                    <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_9f770c67.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_9f770c67.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                );
            case "disconnected":
            case "uninstalled":
                return (
                    <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_a32ff851.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_a32ff851.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                );
            default:
                return (
                    <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_17240952.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_17240952.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                );
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 modal-backdrop" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="relative bg-white dark:bg-neutral-900 border border-transparent dark:border-neutral-800 rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden transform">
                                {/* Header */}
                                <div className="p-6 border-b border-neutral-100 dark:border-neutral-800">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center flex-shrink-0">
                                            {(() => {
                                                if (channel.icon && (channel.icon.startsWith("http") || channel.icon.startsWith("/"))) {
                                                    const secureIcon = channel.icon.startsWith("http:")
                                                        ? channel.icon.replace("http:", "https:")
                                                        : channel.icon;
                                                    return <img src={secureIcon} alt={channel.name} className="w-7 h-7 object-contain" />;
                                                }
                                                return <span className="text-xl">📊</span>;
                                            })()}
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="text-lg font-semibold text-text-primary dark:text-neutral-200 truncate">
                                                {channel.name}
                                            </h3>
                                            <p className="text-sm text-neutral-500 dark:text-neutral-400">
                                                {channel.channelTypeConfig?.name || channel.channelType}
                                            </p>
                                        </div>
                                        <button
                                            onClick={onClose}
                                            className="ml-auto p-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                                        >
                                            <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_9b0e27c1.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_9b0e27c1.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                        </button>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-6 space-y-6">
                                    {/* Status + Connected on */}
                                    <div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <h4 className="text-xs font-semibold text-neutral-500 dark:text-neutral-500 uppercase tracking-wider">
                                                    {t("channels.situation")}
                                                </h4>
                                                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${getStatusColor(channel.status)}`}>
                                                    {getStatusIcon(channel.status)}
                                                    {channel.status === 'syncing' && syncStatusMessage ? (
                                                        syncStatusMessage.startsWith('sync.') ? t(syncStatusMessage) : syncStatusMessage
                                                    ) : (
                                                        t(`channels.${channel.status.toLowerCase() === 'uninstalled' ? 'disconnected' : channel.status.toLowerCase()}`, channel.status)
                                                    )}
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <h4 className="text-xs font-semibold text-neutral-500 dark:text-neutral-500 uppercase tracking-wider">
                                                    {t("channels.addedAt")}
                                                </h4>
                                                <p className="text-sm text-text-primary dark:text-neutral-200">
                                                    {channel.createdAt ? format(new Date(channel.createdAt), "MMM d, yyyy") : '-'}
                                                </p>
                                            </div>
                                        </div>
                                        {channel.syncDetails?.commercialMessage && (
                                            <p className="mt-3 text-xs text-error-500 bg-error-50 dark:bg-error-500/10 p-2 rounded-lg leading-relaxed">
                                                {channel.syncDetails.commercialMessage}
                                            </p>
                                        )}
                                    </div>

                                    <div className="bg-neutral-50 dark:bg-neutral-800/50 p-4 rounded-xl space-y-2">
                                        <h4 className="text-xs font-semibold text-text-primary dark:text-neutral-300">
                                            {t("channels.whatToDo")}
                                        </h4>
                                        <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed font-light">
                                            {i18n.exists(`common:channels.whatToDoDescription_${channel.channelType}`) 
                                                ? t(`channels.whatToDoDescription_${channel.channelType}`) 
                                                : t("channels.whatToDoDescription")}
                                        </p>
                                    </div>

                                    {/* Actions Section */}
                                    <div className="space-y-3 pt-2">
                                        {(channel.status === "error" || channel.status === "disconnected" || channel.status === "uninstalled") ? (
                                            <button
                                                onClick={() => onReconnect(channel)}
                                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-accent-primary text-white text-sm font-medium rounded-xl hover:bg-accent-primary/90 transition-all shadow-sm"
                                            >
                                                <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_1e0a6f5d.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_1e0a6f5d.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                {t("channels.reconnect")}
                                            </button>
                                        ) : canReload ? (
                                            <button
                                                onClick={handleReload}
                                                disabled={isReloading || channel.status === 'syncing'}
                                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-neutral-100 dark:bg-neutral-800 text-text-primary dark:text-neutral-200 text-sm font-medium rounded-xl hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-all disabled:opacity-50"
                                            >
                                                <span className={`w-4 h-4 ${isReloading ? 'animate-spin' : ''}`} style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_741f8e9e.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_741f8e9e.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                {t("channels.reload")}
                                            </button>
                                        ) : null}

                                        <button
                                            onClick={() => {
                                                onClose();
                                                onDelete(channel.id, channel.name);
                                            }}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-error-600 dark:text-error-400 text-sm font-medium rounded-xl hover:bg-error-50 dark:hover:bg-error-500/10 transition-all"
                                        >
                                            <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_ae12a64c.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_ae12a64c.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                            {t("channels.removeChannel")}
                                        </button>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
